import { Box, Chip, Stack, Typography } from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import UndoIcon from "@mui/icons-material/Undo";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import type { Order } from "../../api/orderApi";

type Txn = NonNullable<Order["transactions"]>[number];

function classify(t: Txn): { label: string; icon: JSX.Element; color: "success" | "error" | "secondary" } {
    const isRefund = t.transaction_type === "refund" || Number(t.amount) < 0;
    if (isRefund) {
        return { label: "Refund", icon: <UndoIcon fontSize="small" />, color: "error" };
    }
    if (String(t.payment_method).toLowerCase() === "store_credit") {
        return { label: "Store credit", icon: <CreditCardIcon fontSize="small" />, color: "secondary" };
    }
    return { label: "Payment", icon: <PaymentsIcon fontSize="small" />, color: "success" };
}

export default function OrderActivityTimeline({ order }: { order: Order }) {
    const txns = order.transactions || [];
    const balance = order.payment_balance;

    return (
        <Box sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>Payment activity</Typography>
                {balance?.paymentBalanceStatus && (
                    <Chip
                        size="small"
                        label={balance.paymentBalanceStatus.replace("_", " ")}
                        color={
                            balance.paymentBalanceStatus === "balanced"
                                ? "success"
                                : balance.paymentBalanceStatus === "refund_due"
                                    ? "error"
                                    : balance.paymentBalanceStatus === "balance_due"
                                        ? "warning"
                                        : "default"
                        }
                        sx={{ height: 20, textTransform: "capitalize" }}
                    />
                )}
            </Stack>

            {txns.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No transactions recorded.</Typography>
            ) : (
                <Stack spacing={0.75}>
                    {txns.map((t) => {
                        const { label, icon, color } = classify(t);
                        const amt = Number(t.amount);
                        return (
                            <Box
                                key={t.id}
                                sx={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    gap: 1, p: 0.75, px: 1, border: "1px solid", borderColor: "divider", borderRadius: 1,
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Chip size="small" color={color} icon={icon} label={label} sx={{ height: 22 }} />
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {String(t.payment_method || "").replace("_", " ")}
                                        {" · "}
                                        {new Date(t.created_at).toLocaleString()}
                                    </Typography>
                                </Stack>
                                <Typography fontWeight={700} color={amt < 0 ? "error.main" : "success.main"}>
                                    {amt < 0 ? "-" : "+"}${Math.abs(amt).toFixed(2)}
                                </Typography>
                            </Box>
                        );
                    })}
                    {balance && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Paid ${Number(balance.paidTotal || 0).toFixed(2)}
                                {Number(balance.balanceDue || 0) > 0 && ` · Due $${Number(balance.balanceDue).toFixed(2)}`}
                                {Number(balance.refundDue || 0) > 0 && ` · Refund due $${Number(balance.refundDue).toFixed(2)}`}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            )}
        </Box>
    );
}
